import * as React from "react";
import { Combobox, Option, makeStyles, mergeClasses, useId } from "@fluentui/react-components";
import type { ComboboxProps } from "@fluentui/react-components";

const useStyles = makeStyles({
  listbox: {
    maxHeight: "320px",
    overflowY: "auto",
  },
});

export type FilteredComboBoxProps = Partial<ComboboxProps> & {
  options: Array<[string, string]>;
  selectedOption?: string;
  onValueChange?: (value: string) => void;
};

export const FilteredComboBox = (props: FilteredComboBoxProps): JSX.Element => {
  const styles = useStyles();
  const comboId = useId("combo-default");
  const { options, onValueChange, selectedOption, ...restProps } = props;
  const [matchingOptions, setMatchingOptions] = React.useState([...options]);
  const [customSearch, setCustomSearch] = React.useState<string | undefined>();
  const [inputValue, setInputValue] = React.useState("");

  const selectedLabel = React.useMemo(
    () => options.find((option) => option[0] === selectedOption)?.[1] ?? "",
    [options, selectedOption],
  );

  React.useEffect(() => {
    setMatchingOptions([...options]);
  }, [options]);

  React.useEffect(() => {
    setInputValue(selectedLabel);
  }, [selectedLabel]);

  const onChange: ComboboxProps["onChange"] = (event) => {
    const value = event.target.value.trim();
    setInputValue(value);
    const matches = options.filter((option) => option[1].toLowerCase().includes(value.toLowerCase()));
    setMatchingOptions(matches);
    if (value.length && matches.length < 1) {
      setCustomSearch(value);
    } else {
      setCustomSearch(undefined);
    }
  };

  const onOptionSelect: ComboboxProps["onOptionSelect"] = (_event, data) => {
    const matchingOption =
      (data.optionValue && options.find((option) => option[0] === data.optionValue)) ||
      (data.optionText && options.find((option) => option[1] === data.optionText));

    if (matchingOption) {
      setCustomSearch(undefined);
      setInputValue(matchingOption[1]);
      onValueChange?.(matchingOption[0]);
    } else {
      setCustomSearch(data.optionText);
      setInputValue(data.optionText ?? "");
    }
  };

  return (
    <Combobox
      aria-labelledby={comboId}
      freeform
      placeholder="Select an option"
      inlinePopup={false}
      onChange={onChange}
      onOptionSelect={onOptionSelect}
      value={inputValue}
      selectedOptions={selectedOption ? [selectedOption] : []}
      {...restProps}
      listbox={{ className: mergeClasses(styles.listbox) }}
    >
      {customSearch ? (
        <Option key="freeform" text={customSearch}>
          Search for "{customSearch}"
        </Option>
      ) : null}
      {matchingOptions.map((option) => (
        <Option key={option[0]} value={option[0]} text={option[1]}>
          {option[1]}
        </Option>
      ))}
    </Combobox>
  );
};
